import React from "react";
import axios from "axios";
import { Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Stars({ rating = 0, size = "h-4 w-4" }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= full ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`}
        />
      ))}
    </span>
  );
}

function GoogleG({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function ReviewsPage() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => active && setData(res.data))
      .catch(() => active && setData({ configured: false, reviews: [] }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const reviews = data?.reviews || [];
  const hasReviews = reviews.length > 0;
  const googleUri =
    data?.google_maps_uri ||
    "https://www.google.com/maps/search/?api=1&query=DOLLZ+HAUS+Tralee";

  return (
    <div className="pb-16" data-testid="reviews-page">
      <header className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Reviews
        </p>
        <h1 className="mt-1 text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
          What our clients say
        </h1>
      </header>

      <section className="mb-8 flex flex-col items-start gap-4 rounded-3xl border border-neutral-200 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <GoogleG className="h-8 w-8" />
          <div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-medium text-neutral-900" data-testid="reviews-rating">
                {data?.rating ? Number(data.rating).toFixed(1) : "—"}
              </span>
              <Stars rating={data?.rating || 0} size="h-5 w-5" />
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {data?.user_ratings_total
                ? `Based on ${data.user_ratings_total} Google reviews`
                : "DOLLZ HAUS · Tralee, Co. Kerry"}
            </p>
          </div>
        </div>
        <a
          href={googleUri}
          target="_blank"
          rel="noreferrer"
          data-testid="review-on-google-btn"
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-black px-6 text-[11px] font-medium uppercase tracking-[0.25em] text-white transition-colors hover:bg-neutral-800"
        >
          Review us on Google
        </a>
      </section>

      {loading && <p className="text-xs text-neutral-500">Loading reviews…</p>}

      {!loading && hasReviews && (
        <>
          <div className="grid gap-5 md:grid-cols-2">
            {reviews.map((r, i) => (
              <article
                key={`${r.author_name || "review"}-${r.relative_time || ""}-${i}`}
                data-testid={`review-card-${i}`}
                className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-5"
              >
                <div className="flex items-center gap-3">
                  {r.author_photo_uri ? (
                    <img
                      src={r.author_photo_uri}
                      alt={r.author_name || "Reviewer"}
                      className="h-10 w-10 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-200 text-sm font-medium text-neutral-600">
                      {(r.author_name || "?").charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-neutral-900">
                      {r.author_name || "Google user"}
                    </p>
                    <div className="flex items-center gap-2">
                      <Stars rating={r.rating || 0} />
                      <span className="text-[11px] text-neutral-400">
                        {r.relative_time}
                      </span>
                    </div>
                  </div>
                  <GoogleG className="ml-auto h-4 w-4 shrink-0" />
                </div>
                {r.text && (
                  <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                    {r.text}
                  </p>
                )}
              </article>
            ))}
          </div>
          <p className="mt-6 text-center text-[11px] text-neutral-400">
            Reviews are pulled live from Google and shown in Google&apos;s
            relevance order. Google returns up to 5 reviews.
          </p>
        </>
      )}

      {!loading && !hasReviews && (
        <div className="rounded-3xl border border-dashed border-neutral-300 bg-white p-8 text-center" data-testid="reviews-empty">
          <GoogleG className="mx-auto h-8 w-8" />
          <p className="mt-3 text-sm text-neutral-700">
            Our latest Google reviews will appear here soon.
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            In the meantime, we&apos;d love to hear about your experience.
          </p>
          <a
            href={googleUri}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex h-10 items-center justify-center rounded-full border border-neutral-900 px-6 text-[11px] font-medium uppercase tracking-[0.25em] text-neutral-900 transition-colors hover:bg-neutral-900 hover:text-white"
          >
            Leave us a review
          </a>
        </div>
      )}
    </div>
  );
}
